import { Injectable } from '@angular/core';

// Single shared owner of a screen-wake-lock primitive. Holders identify
// themselves with a tag string; the underlying `acquire` callback fires on
// the first acquire and the `release` callback fires when the last holder
// releases, so overlapping holders (e.g. rest-timer + intake plan-generation
// in test/dev, or nested loaders) coexist without one cancelling the other.
//
// Same tag acquired twice = one entry; release of an unknown tag = no-op.
// Plugin errors are caught and logged but do not propagate — a failing
// acquire must not break the awaited business operation. The refcount
// still advances on failure so release symmetry holds (the matching
// release will also fail / no-op against an OS lock that was never taken).
//
// The constructor takes the acquire/release callbacks rather than referencing
// any plugin directly. This keeps the service file free of Capacitor imports
// so it can be unit-tested without ESM module-loader complications, and
// lets the app.module factory wire whatever underlying implementation the
// platform provides (today: @capacitor-community/keep-awake, which itself
// dispatches per-platform: iOS / Android natively, web via
// navigator.wakeLock; see app.module providers).
@Injectable()
export class WakeLockService {
  private readonly holders = new Set<string>();

  // not DI-resolvable from the parameter types — Angular's injector can't
  // synthesize the function callbacks. Must be wired via `useFactory` in
  // app.module.ts; instantiating WakeLockService directly anywhere else
  // skips the production plugin binding.
  constructor(
    private readonly acquire: () => Promise<void>,
    private readonly release: () => Promise<void>,
  ) {}

  async acquireAsync(tag: string): Promise<void> {
    if (this.holders.has(tag)) return;
    const wasEmpty = this.holders.size === 0;
    this.holders.add(tag);
    if (!wasEmpty) return;
    try {
      await this.acquire();
    } catch (err) {
      // expected: the @capacitor-community/keep-awake plugin rejects on
      // browsers without the Screen Wake Lock API (older Safari, Firefox
      // without the flag). Native failures are vanishingly rare. Either
      // class is high-volume noise for Sentry; keep at console-log per
      // CLAUDE.md's "expected failure modes" carve-out.
      console.log('WakeLockService: keepAwake failed:', err);
      return;
    }
    // race reconciliation: if a concurrent releaseAsync removed our tag while
    // we were awaiting the plugin keepAwake, the plugin lock is now held but
    // the caller has logically released. Drop the dangling lock so the OS
    // state matches our refcount. (Without this, callers like the rest-timer
    // would need their own in-flight tracking to handle a cancel that races
    // an in-progress start.)
    if (!this.holders.has(tag) && this.holders.size === 0) {
      try {
        await this.release();
      } catch (err) {
        // expected: same browsers-without-wake-lock-API rejection as above.
        console.log('WakeLockService: allowSleep (post-acquire reconciliation) failed:', err);
      }
    }
  }

  async releaseAsync(tag: string): Promise<void> {
    if (!this.holders.delete(tag)) return;
    if (this.holders.size !== 0) return;
    try {
      await this.release();
    } catch (err) {
      // expected: same browsers-without-wake-lock-API rejection as acquireAsync.
      console.log('WakeLockService: allowSleep failed:', err);
    }
  }
}
